import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  chatService,
  ChatConversation,
  ChatMessage,
} from '../api/services/chatService';
import { websocketService } from '../api/services/websocketService';

// Helper function to deduplicate messages by ID
const deduplicateMessages = (messages: ChatMessage[]): ChatMessage[] => {
  return messages.filter(
    (msg, index, self) => index === self.findIndex(m => m.id === msg.id)
  );
};

// Helper function to deduplicate conversations by ID
const deduplicateConversations = (
  conversations: ChatConversation[]
): ChatConversation[] => {
  return conversations.filter(
    (conv, index, self) => index === self.findIndex(c => c.id === conv.id)
  );
};

export const useChat = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversation, setActiveConversation] =
    useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Use ref to track active conversation to avoid stale closures in WebSocket handlers
  const activeConversationRef = useRef<ChatConversation | null>(null);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await chatService.getConversations();

      // Remove duplicates by ID to prevent React key warnings
      const uniqueConversations = deduplicateConversations(data);

      setConversations(uniqueConversations);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversations');
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId: string) => {
    try {
      setLoading(true);
      const data = await chatService.getMessages(conversationId);

      // Remove duplicates by ID, then sort by createdAt timestamp
      const uniqueMessages = deduplicateMessages(data.messages);

      const sortedMessages = uniqueMessages.sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return timeA - timeB; // Ascending order (oldest first)
      });

      setMessages(sortedMessages);
      // Mark as read
      await chatService.markAsRead(conversationId);
      // Update conversation unread count
      setConversations(prev =>
        prev.map(c => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to fetch messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectConversation = useCallback(
    async (conversation: ChatConversation) => {
      // Leave previous conversation room if exists
      if (activeConversation?.id) {
        websocketService.leaveConversation(activeConversation.id);
      }

      // Update active conversation immediately
      setActiveConversation(conversation);
      // Update ref immediately so WebSocket handlers have correct value
      activeConversationRef.current = conversation;

      // Join conversation room for better message delivery (Socket.IO feature)
      websocketService.joinConversation(conversation.id);

      // Always refetch messages when selecting a conversation to ensure we have latest
      await fetchMessages(conversation.id);

      // Mark as read
      await chatService.markAsRead(conversation.id).catch(err => {
        console.error('Failed to mark conversation as read:', err);
      });

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },
    [fetchMessages, activeConversation]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!activeConversation || !content.trim()) return;

      try {
        setSending(true);
        const newMessage = await chatService.sendMessage(
          activeConversation.id,
          content.trim()
        );

        // Insert message in correct chronological position (with duplicate check)
        setMessages(prev => {
          // Check if message already exists (prevent duplicates from WebSocket)
          const exists = prev.find(m => m.id === newMessage.id);
          if (exists) {
            console.log(
              '⚠️ Message already exists in sendMessage, skipping duplicate'
            );
            return prev;
          }

          const newMessages = [...prev, newMessage];
          // Remove any duplicates, then sort by timestamp
          const unique = deduplicateMessages(newMessages);

          return unique.sort((a, b) => {
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();
            return timeA - timeB; // Ascending order (oldest first)
          });
        });

        // Update conversation last message
        setConversations(prev =>
          prev.map(c =>
            c.id === activeConversation.id
              ? {
                  ...c,
                  lastMessage: {
                    id: newMessage.id,
                    content: newMessage.content,
                    senderId: newMessage.senderId,
                    createdAt: newMessage.createdAt,
                    read: newMessage.read,
                  },
                  updatedAt: newMessage.createdAt,
                }
              : c
          )
        );
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } catch (err: any) {
        setError(err.message || 'Failed to send message');
        console.error('Error sending message:', err);
      } finally {
        setSending(false);
      }
    },
    [activeConversation]
  );

  const startConversation = useCallback(
    async (listingId: string, initialMessage?: string) => {
      try {
        setLoading(true);

        // First, check if a conversation already exists for this listing
        // This prevents creating duplicate conversations
        const existingConversations = await chatService.getConversations();
        const existingConversation = existingConversations.find(
          conv => conv.listingId === listingId
        );

        let conversation;
        if (existingConversation) {
          // Use existing conversation
          conversation = existingConversation;
        } else {
          // Create new conversation
          conversation = await chatService.getOrCreateConversation(
            listingId,
            initialMessage
          );
        }

        // Add to conversations if not already there (prevent duplicates)
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversation.id);
          if (exists) {
            // Update existing conversation instead of adding duplicate
            return prev.map(c => (c.id === conversation.id ? conversation : c));
          }
          // Add new conversation to the top, then remove any duplicates
          const updated = [conversation, ...prev];
          return deduplicateConversations(updated);
        });
        setActiveConversation(conversation);
        await fetchMessages(conversation.id);
        // Navigate to messages page with conversation ID
        navigate(`/dashboard/messages?conversation=${conversation.id}`);
      } catch (err: any) {
        setError(err.message || 'Failed to start conversation');
        console.error('Error starting conversation:', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchMessages, navigate]
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await chatService.deleteConversation(conversationId);
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        if (activeConversation?.id === conversationId) {
          setActiveConversation(null);
          activeConversationRef.current = null;
          setMessages([]);
        }
      } catch (err) {
        console.error('Error deleting conversation:', err);
      }
    },
    [activeConversation]
  );

  // Update ref whenever activeConversation changes
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    fetchConversations();

    // Subscribe to WebSocket messages
    const unsubscribeMessage = websocketService.onMessage(
      (message: ChatMessage) => {
        console.log('📨 WebSocket message received:', message);

        // Use ref to get current active conversation (avoids stale closure issue)
        const currentActiveConversation = activeConversationRef.current;

        // If this message belongs to the active conversation, add it to messages
        if (currentActiveConversation?.id === message.conversationId) {
          console.log(
            '✅ Message belongs to active conversation, adding to messages'
          );
          setMessages(prev => {
            // Check if message already exists (prevent duplicates)
            const exists = prev.find(m => m.id === message.id);
            if (exists) {
              console.log(
                '⚠️ Message already exists in WebSocket handler, skipping duplicate'
              );
              return prev;
            }

            const newMessages = [...prev, message];
            // Remove any duplicates first, then sort by timestamp
            const unique = deduplicateMessages(newMessages);

            return unique.sort((a, b) => {
              const timeA = new Date(a.createdAt).getTime();
              const timeB = new Date(b.createdAt).getTime();
              return timeA - timeB; // Ascending order (oldest first)
            });
          });

          // Mark as read if it's the active conversation
          if (currentActiveConversation.id === message.conversationId) {
            chatService.markAsRead(message.conversationId).catch(err => {
              console.error('Failed to mark message as read:', err);
            });
          }

          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          console.log(
            'ℹ️ Message belongs to different conversation:',
            message.conversationId,
            'Active:',
            currentActiveConversation?.id
          );
        }

        // Always update conversation list (for both active and inactive conversations)
        setConversations(prev => {
          // Remove duplicates first, then update
          const unique = deduplicateConversations(prev);

          return unique.map(c => {
            if (c.id === message.conversationId) {
              const isActive =
                currentActiveConversation?.id === message.conversationId;
              return {
                ...c,
                lastMessage: {
                  id: message.id,
                  content: message.content,
                  senderId: message.senderId,
                  createdAt: message.createdAt,
                  read: message.read,
                },
                unreadCount: isActive ? 0 : c.unreadCount + 1,
                updatedAt: message.createdAt,
              };
            }
            return c;
          });
        });
      }
    );

    // Subscribe to WebSocket notifications for new conversations
    const unsubscribeNotification = websocketService.onNotification(
      (notification: any) => {
        console.log('🔔 WebSocket notification received:', notification);

        // Check if this is a new conversation notification
        if (
          notification.type === 'new_conversation' &&
          notification.conversation
        ) {
          const newConversation = notification.conversation as ChatConversation;

          // Check if conversation already exists in the list and prevent duplicates
          setConversations(prev => {
            // First remove any duplicates
            const unique = deduplicateConversations(prev);

            const exists = unique.find(c => c.id === newConversation.id);
            if (exists) {
              // Update existing conversation
              return unique.map(c =>
                c.id === newConversation.id ? { ...newConversation, ...c } : c
              );
            } else {
              // Add new conversation to the top of the list
              return [newConversation, ...unique];
            }
          });
        }
      }
    );

    return () => {
      unsubscribeMessage();
      unsubscribeNotification();
    };
  }, [fetchConversations]); // Removed activeConversation from dependencies to avoid recreating handlers

  return {
    conversations,
    activeConversation,
    messages,
    loading,
    sending,
    error,
    messagesEndRef,
    selectConversation,
    sendMessage,
    startConversation,
    deleteConversation,
    refetch: fetchConversations,
  };
};
